import React, { useEffect, useState, useRef, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { getHeaders, cancelCart } from '../api';
import CartBuilderModal from './CartBuilderModal';
import ProspectInviteBoard from './ProspectInvites';
import { StageChangeModal, LostReasonsPanel } from '../components/DealStage';
import TodayRail from '../components/TodayRail';
import Overnight from '../components/Overnight';
import BonusGuide from '../components/BonusGuide';
import { formatSfDate } from '../lib/dates';
import { refundsState, netLabel, netLabelTitle, refundedNote, orderAmount, money as netMoney } from '../lib/netRevenue';

const API = import.meta.env.PROD ? '' : 'https://crm-api.squish.co';

// ─── Utilities ───────────────────────────────────────────────────────────────
function Loading() {
  return <div className="loading-state"><span className="loading-dot" /><span className="loading-dot" /><span className="loading-dot" /></div>;
}

function fmt$(v) {
  if (v == null) return '—';
  return `$${parseFloat(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Same calendar-day-vs-instant trap the customer page had. See lib/dates.js.
const fmtDate = (d) => formatSfDate(d, { year: false });

function hoursAgo(iso) {
  if (!iso) return 0;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 3600000);
}

function fmtTimeAgo(iso) {
  if (!iso) return '—';
  const h = hoursAgo(iso);
  if (h < 1) return 'just now';
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function fmtFirestoreDate(v) {
  if (!v) return '—';
  if (typeof v === 'object' && v._seconds) return fmtDate(new Date(v._seconds * 1000));
  return fmtDate(v);
}

// Compact money for the KPI strip. Six numbers share one row, and a cell is
// ~165px at the narrowest layout that still fits six — "$1,287.00" overflows
// it, "$1,287" does not. Cents survive below $1,000, where they are the
// difference between $5.70 and $5; above it they are noise.
function fmt$c(v) {
  if (v == null) return '—';
  const n = parseFloat(v);
  if (!isFinite(n)) return '—';
  if (Math.abs(n) >= 10000) return `$${(n / 1000).toFixed(1)}k`;
  // Cents below $100 only: seven tiles share the strip now, and ".00" on a
  // four-figure number is what pushes the value past its cell.
  if (Math.abs(n) >= 100) return `$${Math.round(n).toLocaleString('en-US')}`;
  return fmt$(n);
}

// Chart axis ticks. The old formatter was `$${(v/1000).toFixed(0)}k`, which
// prints "$0k" for every tick until monthly revenue clears $500 — the axis
// carried no information at our current scale. Scale to the data instead.
function axisMoney(v) {
  if (Math.abs(v) >= 10000) return `$${(v / 1000).toFixed(0)}k`;
  if (Math.abs(v) >= 1000)  return `$${(v / 1000).toFixed(1)}k`;
  return `$${Math.round(v)}`;
}

// ─── KPI strip ─────────────────────────────────────────────────────────────────
// One dense row instead of two sparse ones. Every cell carries a subline, so
// the numbers that used to need their own card (high-priority count, revenue
// today, deals closing) ride along with the number they qualify.
// `title` is the tile's hover text — where a net figure says what it is net of.
function Kpi({ label, value, tone, sub, subTone, highlight, action, title }) {
  return (
    <div className={`kpi${highlight ? ' kpi-hi' : ''}`} title={title || undefined}>
      {/* `action` sits in the corner rather than after the label, so a long
          label wrapping cannot push it out of the cell. */}
      {action}
      <div className="kpi-label">{label}</div>
      <div className={`kpi-value${tone ? ` ${tone}` : ''}`}>{value}</div>
      {/* Always rendered — a missing subline would make that cell short and
          break the row's baseline. */}
      <div className={`kpi-sub${subTone ? ` ${subTone}` : ''}`}>{sub || '\u00a0'}</div>
    </div>
  );
}

export function KpiStrip({ stats, sales, staff, isAdmin }) {
  const [guideOpen, setGuideOpen] = useState(false);
  // Every figure here comes from `stats`, which the backend scopes to the
  // caller. The company-wide `analytics` block must not leak in: its
  // highPriorityCases under an owner-scoped Open Cases count would read
  // "0 open cases · 3 high priority".
  const highPriority = stats?.highPriorityCases ?? 0;
  const closing      = (sales?.closingThisWeek || []).length;
  const draftValue   = (sales?.draftCarts || []).reduce((sum, c) => sum + (parseFloat(c.total) || 0), 0);
  const bonusPct     = ((stats?.bonusPct ?? 0) * 100).toFixed(0);
  // 'all' means the backend could not resolve the caller to a Salesforce User.
  // Say so rather than presenting company figures under personal labels — that
  // confusion is the thing this strip was changed to stop.
  const scoped = stats?.scope !== 'all';
  // The money tiles come from Order.Placed_By__c — orders THIS staffer placed.
  // Two ways that can have no answer, and each says so on the tile rather
  // than showing a 0 that reads as "sold nothing":
  //   no-staff-user  the caller has no Salesforce User to attribute to
  //   unavailable    the attribution fields aren't readable yet (runbook)
  const attribution = stats?.attribution || 'ok';
  const moneySub = attribution === 'ok' ? null
    : attribution === 'no-staff-user' ? 'no Salesforce user'
    : 'not measured yet';
  const money = (v) => (attribution === 'ok' ? fmt$c(v ?? 0) : '\u2014');
  // How much of this month's bonus rests on a family-target estimate rather
  // than a sourced supplier cost. Every M365 line does today, so this is not a
  // rare edge — a rep is entitled to know which part of their number is a guess.
  const estimated = stats?.estimatedOrders ?? 0;
  const bonusSub = moneySub
    || (estimated > 0 ? `${bonusPct}% \u00b7 ${estimated} estimated` : `${bonusPct}% of your margin`);

  // The two revenue tiles are NET of refunds when the backend says so
  // (stats.refunds === 'ok'), and say what came off. When it could not read
  // the refunded amount they are gross, and the tile says "gross" in the warn
  // tone rather than letting a gross number sit under a revenue label that now
  // means net everywhere else. See lib/netRevenue.js. Margin and the bonus are
  // untouched: they were already net of refunds.
  const refunds = attribution === 'ok' ? refundsState(stats) : 'unknown';
  const todayNote = refundedNote({ state: refunds, gross: stats?.todayRevenueGross, refunded: stats?.todayRefunded }, { compact: true });
  const monthNote = refundedNote({ state: refunds, gross: stats?.monthRevenueGross, refunded: stats?.monthRefunded }, { compact: true });
  const monthCount = stats?.monthOrders != null ? `${stats.monthOrders} order${stats.monthOrders === 1 ? '' : 's'}` : null;
  const todaySub = moneySub
    || (todayNote ? (todayNote.warn ? 'gross \u00b7 refunds unavailable' : todayNote.text) : 'orders you placed');
  const monthSub = moneySub
    || (monthNote
      ? (monthNote.warn ? `${monthCount ? `${monthCount} \u00b7 ` : ''}gross` : `${monthCount ? `${monthCount} \u00b7 ` : ''}${monthNote.text}`)
      : (monthCount ? `${monthCount} \u00b7 month to date` : 'month to date'));
  const revenueTone = moneySub || refunds === 'unavailable' ? 'warn' : undefined;
  const scopeNet = scoped && refunds !== 'unknown' ? ` \u00b7 revenue ${netLabel(refunds)}` : '';

  return (
    <>
      <div className={`kpi-scope${scoped ? '' : ' kpi-scope-warn'}`} title={scoped ? netLabelTitle(refunds) : undefined}>
        {scoped
          ? `Your work${staff?.name ? ` \u00b7 ${staff.name}` : ''}${scopeNet}`
          : 'Company-wide \u2014 we could not match you to a Salesforce user'}
      </div>
      <div className="kpi-strip">
      <Kpi
        label="Open Cases" value={stats.openCases ?? 0} tone="pink"
        sub={highPriority > 0 ? `${highPriority} high priority` : 'none high priority'}
        subTone={highPriority > 0 ? 'warn' : undefined}
      />
      <Kpi
        label="Deals In Progress" value={stats.dealsInProgress ?? 0} tone="accent"
        sub={closing > 0 ? `${closing} closing this week` : 'none closing this week'}
      />
      <Kpi
        label="Draft Carts" value={stats.draftCarts ?? 0} tone="yellow"
        sub={`${fmt$c(draftValue)} in drafts`}
      />
      <Kpi
        label="Revenue Today" value={money(stats?.todayRevenue)} tone="green"
        sub={todaySub} subTone={revenueTone} title={todayNote?.title}
      />
      <Kpi
        label="Sales This Month" value={money(stats?.monthRevenue)} tone="green"
        sub={monthSub} subTone={revenueTone} title={monthNote?.title}
      />
      {/* Margin is what the bonus is actually paid on, so it sits next to the
          revenue it is derived from rather than being left for the rep to
          infer from a percentage. */}
      <Kpi
        label="Margin This Month" value={money(stats?.monthMargin)} tone="accent"
        sub={moneySub || 'after cost + fees'} subTone={moneySub ? 'warn' : undefined}
      />
      {/* Everyone sees their own bonus — it is a share of what THEY sold — and
          everyone can open the plan that produced it. A number nobody can
          check is a number people distrust. */}
      <Kpi
        label="Bonus Projection" value={money(stats?.bonusProjection)} tone="yellow" highlight
        sub={bonusSub} subTone={moneySub ? 'warn' : estimated > 0 ? 'warn' : undefined}
        action={
          <button
            className="kpi-action"
            onClick={() => setGuideOpen(true)}
            title="How this is calculated"
            aria-label="How this bonus is calculated"
          >
            ?
          </button>
        }
      />
      </div>
      {guideOpen && <BonusGuide onClose={() => setGuideOpen(false)} />}
    </>
  );
}

function priorityBadge(p) {
  return ({ High: 'badge-red', Medium: 'badge-yellow', Low: 'badge-green' })[p] || 'badge-gray';
}

function statusBadge(s) {
  return ({
    New: 'badge-blue', Working: 'badge-yellow', Escalated: 'badge-red',
    Closed: 'badge-green', 'Closed Won': 'badge-green', 'Closed Lost': 'badge-red',
    Prospecting: 'badge-blue', Qualification: 'badge-blue', 'Needs Analysis': 'badge-yellow',
    'Value Proposition': 'badge-yellow', 'Proposal/Price Quote': 'badge-yellow',
    Negotiation: 'badge-yellow',
  })[s] || 'badge-gray';
}

function roleLabel(role) {
  return ({ admin: 'Admin', sales: 'Sales', support: 'Support', unknown: 'Guest' })[role] || 'Guest';
}

function roleBadgeColor(role) {
  return ({ admin: '#fac850', sales: '#7c6dfa', support: '#4ade80', unknown: '#9090a8' })[role] || '#9090a8';
}

// ─── Customer Search (dashboard header) ──────────────────────────────────────
function CustomerSearch({ onPick }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Keyboard shortcut: "/" focuses the search bar
  useEffect(() => {
    function onKey(e) {
      if (e.key === '/' && !['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName)) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!q.trim() || q.trim().length < 2) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/search?q=${encodeURIComponent(q)}`, { headers: getHeaders() });
        const data = await res.json();
        setResults(Array.isArray(data) ? data.slice(0, 8) : []);
      } catch (_) { setResults([]); }
      setLoading(false);
    }, 180);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  return (
    <div style={{ position: 'relative', minWidth: 320, flex: 1, maxWidth: 520 }}>
      <input
        ref={inputRef}
        value={q}
        onChange={e => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder='Search customers — press "/" to focus'
        style={{
          width: '100%', padding: '9px 14px', background: 'var(--bg)', border: '1px solid var(--border)',
          borderRadius: 8, color: 'var(--text)', fontSize: 13, outline: 'none', fontFamily: 'var(--font)',
          boxSizing: 'border-box',
        }}
      />
      {open && q.trim().length >= 2 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
          background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8,
          boxShadow: '0 10px 40px rgba(0,0,0,0.4)', zIndex: 100, maxHeight: 360, overflowY: 'auto',
        }}>
          {loading && <div style={{ padding: 12, color: 'var(--text3)', fontSize: 12 }}>Searching…</div>}
          {!loading && results.length === 0 && <div style={{ padding: 12, color: 'var(--text3)', fontSize: 12 }}>No matches.</div>}
          {results.map(r => (
            <div
              key={r.Id}
              onMouseDown={e => { e.preventDefault(); onPick?.(r); setQ(''); setOpen(false); }}
              style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ color: 'var(--text)', fontWeight: 600, fontSize: 13 }}>{r.Name}</div>
              <div style={{ color: 'var(--text3)', fontSize: 11, fontFamily: 'var(--mono)', marginTop: 2 }}>
                #{r.AccountNumber || '—'} · {r.BillingCity || '—'}{r.BillingState ? `, ${r.BillingState}` : ''} · {r.Type || 'Account'}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Support Tab ─────────────────────────────────────────────────────────────
function SupportTab({ support, onPickCustomer, onNavigateToTicket }) {
  const myQueue = support?.myQueue || [];
  const slaAlerts = support?.slaAlerts || [];

  return (
    <div className="m-stack" style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 16 }}>
      <div className="table-card">
        <div className="table-header">
          <span className="table-title">My Queue</span>
          <span className="table-count">{myQueue.length}</span>
        </div>
        {myQueue.length === 0 ? (
          <div style={{ padding: 22, textAlign: 'center', color: 'var(--accent3)', fontSize: 12, fontFamily: 'var(--mono)' }}>
            ✓ No open cases assigned to you
          </div>
        ) : (
          <table className="m-cards">
            <thead><tr><th>Case #</th><th>Subject</th><th>Account</th><th>Priority</th><th>Opened</th></tr></thead>
            <tbody>
              {myQueue.map(c => (
                // Row click opens the case in the Tickets modal (the action a
                // rep coming to this queue actually wants). Account name cell
                // overrides with stopPropagation + navigates to the customer
                // page for the rare "I want context on this account" case.
                <tr key={c.Id} style={{ cursor: 'pointer' }} onClick={() => onNavigateToTicket?.(c.Id)}>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--accent)' }}>{c.CaseNumber}</td>
                  <td className="m-card-title" style={{ color: 'var(--text)', fontWeight: 600, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.Subject || '—'}</td>
                  <td
                    style={{ fontSize: 12, color: c.Account ? 'var(--accent)' : 'var(--text2)', cursor: c.Account ? 'pointer' : 'default' }}
                    onClick={e => { if (c.Account) { e.stopPropagation(); onPickCustomer?.(c.Account); } }}
                    title={c.Account ? 'Open account' : undefined}
                  >
                    {c.Account?.Name || '—'}
                  </td>
                  <td><span className={`badge ${priorityBadge(c.Priority)}`}>{c.Priority || '—'}</span></td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text3)' }}>{fmtTimeAgo(c.CreatedDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="table-card">
        <div className="table-header">
          <span className="table-title">SLA Alerts</span>
          <span className="table-count">{slaAlerts.length}</span>
        </div>
        {slaAlerts.length === 0 ? (
          <div style={{ padding: 22, textAlign: 'center', color: 'var(--accent3)', fontSize: 12, fontFamily: 'var(--mono)' }}>
            ✓ All open cases responded in 24h
          </div>
        ) : (
          <div>
            {slaAlerts.map(c => {
              const hrs = hoursAgo(c.LastModifiedDate);
              return (
                <div
                  key={c.Id}
                  onClick={() => onNavigateToTicket?.(c.Id)}
                  style={{ padding: '10px 16px', borderBottom: '1px solid rgba(42,42,56,0.5)', cursor: 'pointer' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.Subject || '—'}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', marginTop: 2 }}>
                        <span
                          onClick={e => { if (c.Account) { e.stopPropagation(); onPickCustomer?.(c.Account); } }}
                          style={{ cursor: c.Account ? 'pointer' : 'default', color: c.Account ? 'var(--accent)' : 'var(--text3)' }}
                          title={c.Account ? 'Open account' : undefined}
                        >
                          {c.Account?.Name || 'No account'}
                        </span>
                        {' · '}{c.CaseNumber}
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: hrs > 72 ? '#f87171' : '#fac850', fontFamily: 'var(--mono)', fontWeight: 700, flexShrink: 0 }}>
                      {hrs}h stale
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Sales Tab ───────────────────────────────────────────────────────────────
// One row of the Sales tab's Draft Carts table. Clicking the row opens the
// customer; Cancel is the same DELETE /api/carts/:id as the customer's Draft
// Carts section and the invites board, so squish-backend still refuses a paid
// cart and withdraws its PaymentIntent. The cart is kept for audit and marked
// cancelled, which takes it off this list (the dashboard lists drafts only).
function DraftCartRow({ cart: c, onPickCustomer, onCancelled }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function cancel() {
    setBusy(true);
    setError('');
    try {
      await cancelCart(c.id);
      setConfirming(false);
      onCancelled?.();
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  }

  return (
    <tr
      style={{ cursor: c.accountId ? 'pointer' : 'default' }}
      onClick={() => c.accountId && onPickCustomer?.({ Id: c.accountId, Name: c.accountName })}
    >
      <td style={{ color: 'var(--text)', fontWeight: 600, fontSize: 12 }}>{c.accountName || '—'}</td>
      <td style={{ fontSize: 11, color: 'var(--text3)' }}>{(c.items || []).length}</td>
      <td style={{ fontFamily: 'var(--mono)', color: 'var(--accent3)', fontSize: 12 }}>{fmt$(c.total)}</td>
      <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text3)' }}>{fmtFirestoreDate(c.createdAt)}</td>
      {/* Clicks in this cell never reach the row, so Cancel does not also
          open the customer. */}
      <td style={{ textAlign: 'right' }} onClick={e => e.stopPropagation()}>
        {confirming ? (
          <span className="inv-card-confirm" data-write style={{ justifyContent: 'flex-end' }}>
            Cancel this cart?
            <button className="inv-btn" disabled={busy} onClick={() => setConfirming(false)}>Keep</button>
            <button className="inv-btn is-danger is-primary" disabled={busy} onClick={cancel}>
              {busy ? '…' : 'Yes, cancel'}
            </button>
          </span>
        ) : (
          <button className="inv-btn is-danger" data-write onClick={() => { setError(''); setConfirming(true); }}>
            Cancel
          </button>
        )}
        {error && <div style={{ color: '#f87171', fontSize: 11, marginTop: 4 }}>⚠ {error}</div>}
      </td>
    </tr>
  );
}

function SalesTab({ sales, onPickCustomer, onDealChanged, onCartCancelled, invitesVersion }) {
  const dealsByStage = sales?.dealsByStage || {};
  const closingThisWeek = sales?.closingThisWeek || [];
  const draftCarts = sales?.draftCarts || [];
  const stages = Object.keys(dealsByStage);
  const totalMyDeals = Object.values(dealsByStage).reduce((sum, arr) => sum + arr.length, 0);
  const [stageDeal, setStageDeal] = useState(null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {stageDeal && (
        <StageChangeModal
          opp={stageDeal}
          onClose={() => setStageDeal(null)}
          onSaved={() => onDealChanged?.()}
        />
      )}
      <ProspectInviteBoard refreshKey={invitesVersion} />

      {/* Kanban of my deals by stage */}
      <div className="table-card">
        <div className="table-header">
          <span className="table-title">My Pipeline</span>
          <span className="table-count">{totalMyDeals}</span>
        </div>
        {stages.length === 0 ? (
          <div style={{ padding: 22, textAlign: 'center', color: 'var(--text3)', fontSize: 12, fontFamily: 'var(--mono)' }}>
            No open deals assigned to you
          </div>
        ) : (
          <div style={{ padding: 16, display: 'grid', gridTemplateColumns: `repeat(${Math.min(stages.length, 4)}, 1fr)`, gap: 12, overflowX: 'auto' }}>
            {stages.map(stage => (
              <div key={stage} style={{ minWidth: 200 }}>
                <div style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8, fontWeight: 600 }}>
                  {stage} · {dealsByStage[stage].length}
                </div>
                {dealsByStage[stage].map(d => (
                  <div
                    key={d.Id}
                    onClick={() => d.Account && onPickCustomer?.(d.Account)}
                    style={{ padding: 10, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 6, marginBottom: 6, cursor: 'pointer' }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.Name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{d.Account?.Name || '—'}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                      <span style={{ fontSize: 12, color: 'var(--accent3)', fontWeight: 700, fontFamily: 'var(--mono)' }}>{fmt$(d.Amount)}</span>
                      <span style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>close {fmtDate(d.CloseDate)}</span>
                    </div>
                    {/* stopPropagation: the card itself navigates to the account. */}
                    <button data-write
                      onClick={e => { e.stopPropagation(); setStageDeal(d); }}
                      style={{ marginTop: 8, width: '100%', padding: '4px 8px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 5, color: 'var(--text3)', fontSize: 10, fontFamily: 'var(--mono)', cursor: 'pointer' }}
                    >
                      change stage
                    </button>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Two-column: closing this week + draft carts */}
      <div className="m-stack" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div className="table-card">
          <div className="table-header">
            <span className="table-title">Closing This Week</span>
            <span className="table-count">{closingThisWeek.length}</span>
          </div>
          {closingThisWeek.length === 0 ? (
            <div style={{ padding: 22, textAlign: 'center', color: 'var(--text3)', fontSize: 12, fontFamily: 'var(--mono)' }}>
              No deals closing this week
            </div>
          ) : (
            <table>
              <thead><tr><th>Deal</th><th>Owner</th><th>Amount</th><th>Close</th></tr></thead>
              <tbody>
                {closingThisWeek.map(d => (
                  <tr key={d.Id} style={{ cursor: 'pointer' }} onClick={() => d.Account && onPickCustomer?.(d.Account)}>
                    <td style={{ color: 'var(--text)', fontWeight: 600, fontSize: 12, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.Name}</td>
                    <td style={{ fontSize: 11, color: 'var(--text3)' }}>{d.Owner?.Name || '—'}</td>
                    <td style={{ fontFamily: 'var(--mono)', color: 'var(--accent3)', fontSize: 12 }}>{fmt$(d.Amount)}</td>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text3)' }}>{fmtDate(d.CloseDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="table-card">
          <div className="table-header">
            <span className="table-title">Draft Carts</span>
            <span className="table-count">{draftCarts.length}</span>
          </div>
          {draftCarts.length === 0 ? (
            <div style={{ padding: 22, textAlign: 'center', color: 'var(--text3)', fontSize: 12, fontFamily: 'var(--mono)' }}>
              No draft carts yet — build one from a customer's Carts tab
            </div>
          ) : (
            <table>
              <thead><tr><th>Account</th><th>Items</th><th>Total</th><th>Created</th><th /></tr></thead>
              <tbody>
                {draftCarts.slice(0, 10).map(c => (
                  <DraftCartRow key={c.id} cart={c} onPickCustomer={onPickCustomer} onCancelled={onCartCancelled} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Analytics Tab ───────────────────────────────────────────────────────────
export const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  // The month's own row. `refunded` is what came off that month's sales: null
  // when refunds are unavailable, undefined from a backend before the change.
  const refunded = payload[0]?.payload?.refunded;
  return (
    <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', fontFamily: 'DM Mono, monospace', fontSize: 12 }}>
      <div style={{ color: 'var(--text2)', marginBottom: 4 }}>{label}</div>
      <div style={{ color: 'var(--accent)', fontWeight: 700 }}>{fmt$(payload[0].value)}</div>
      {refunded > 0 && <div style={{ color: 'var(--badge-yellow)', marginTop: 2 }}>{'−'}{netMoney(refunded)} refunded</div>}
    </div>
  );
};

// The company-wide numbers (whole-business strip, revenue by channel and by
// rep) live on Administration › Sales now — the CEO's page, not the daily one.
// This tab keeps what every admin wants next to the queues: the trend, why we
// lose, and what just sold.
export function AnalyticsTab({ analytics, onPickCustomer }) {
  const mrrTrend = analytics?.mrrTrend || [];
  const recentOrders = analytics?.recentOrders || [];
  const trendTotal = mrrTrend.reduce((sum, m) => sum + (m.revenue || 0), 0);
  // Net of refunds, gross, or a backend that never said — one phrase on the
  // chart, and each order's refund under its total. See lib/netRevenue.js.
  const refunds = refundsState(analytics);
  const label = netLabel(refunds);
  const labelTitle = netLabelTitle(refunds, analytics?.refundsReason);

  // A rep column is dead weight on an all-self-serve list, so it only appears
  // once something in view actually has one.
  const anyRep = recentOrders.some(o => o.placedByRep);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Chart and the loss breakdown share a row — the chart never needed
          1300px, and "Why We Lose" is usually a short list or an empty note,
          so stacking them left a band of dead space on every load. */}
      <div className="analytics-cols">
        <div className="chart-card">
          <div className="chart-head">
            <span className="chart-title">Revenue Trend</span>
            <span className="chart-meta" title={labelTitle} style={refunds === 'unavailable' ? { color: 'var(--badge-yellow)' } : undefined}>
              last 6 months · {fmt$c(trendTotal)}{label ? ` · ${label}` : ''}
            </span>
          </div>
          {mrrTrend.length === 0 ? (
            <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text3)', fontFamily: 'var(--mono)', fontSize: 12 }}>No order data yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={mrrTrend} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                <CartesianGrid stroke="#2a2a38" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: '#5a5a72', fontSize: 10, fontFamily: 'DM Mono' }} axisLine={false} tickLine={false} />
                <YAxis width={54} tick={{ fill: '#5a5a72', fontSize: 10, fontFamily: 'DM Mono' }} axisLine={false} tickLine={false} tickFormatter={axisMoney} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(124,109,250,0.08)' }} />
                {/* isAnimationActive={false}: inside .analytics-cols the container is
                    measured twice on mount (grid placeholder width, then the real
                    track). Recharts animates the bars from the first width and
                    leaves them at that scale, so bars end up bunched under a
                    correctly-spread axis. No animation, no stale scale. */}
                <Bar dataKey="revenue" fill="#7c6dfa" radius={[4, 4, 0, 0]} maxBarSize={72} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Why we lose — the evidence surface for the parked pricing decisions
            (multi-year discount ladder, entry-level VPS $15-35). It answers a
            question rather than just reporting one, so it keeps top billing. */}
        <LostReasonsPanel />
      </div>

      {/* Recent orders */}
      <div className="table-card">
        <div className="table-header"><span className="table-title">Recent Orders</span><span className="table-count">{recentOrders.length}</span></div>
        <table>
          <thead><tr>
            <th>Order #</th><th>Account</th><th>Products</th>
            {anyRep && <th>Placed by</th>}
            <th title={labelTitle}>{refunds === 'ok' ? 'Total (net)' : 'Total'}</th><th>Date</th>
          </tr></thead>
          <tbody>
            {recentOrders.length === 0 && <tr><td colSpan={anyRep ? 6 : 5} style={{ textAlign: 'center', color: 'var(--text3)', padding: 22 }}>No orders yet</td></tr>}
            {recentOrders.map(o => (
              <tr key={o.Id} style={{ cursor: 'pointer' }} onClick={() => o.Account && onPickCustomer?.(o.Account)}>
                <td style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--accent)', fontWeight: 700 }}>{o.OrderNumber}</td>
                <td style={{ color: 'var(--text)', fontWeight: 600 }}>{o.Account?.Name || '—'}</td>
                <td style={{ color: 'var(--text2)', fontSize: 12 }}>{o.OrderItems?.records?.map(i => i.Product2?.Name || '—').join(', ') || '—'}</td>
                {anyRep && (
                  // "Self-serve" rather than a dash: the customer placing it is
                  // a fact about the order, not a missing value.
                  <td style={{ fontSize: 12, color: o.placedByRep ? 'var(--text2)' : 'var(--text3)', fontFamily: o.placedByRep ? 'var(--font)' : 'var(--mono)' }}>
                    {o.placedByRep || 'self-serve'}
                  </td>
                )}
                {/* The order's total less what went back, with the refund
                    under it — same treatment as the account page's orders. */}
                <td style={{ fontFamily: 'var(--mono)', color: 'var(--accent3)', fontWeight: 600 }} title={orderAmount(o, refunds).title}>
                  {fmt$(orderAmount(o, refunds).amount)}
                  {orderAmount(o, refunds).refunded > 0 && (
                    <div style={{ fontSize: 10, color: 'var(--badge-yellow)', fontWeight: 400 }}>{'−'}{netMoney(orderAmount(o, refunds).refunded)} refunded</div>
                  )}
                </td>
                <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text3)' }}>{fmtDate(o.EffectiveDate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Prospect Cart Launcher ──────────────────────────────────────────────────
// Two-step: collect the prospect's email/name/company, then open the regular
// CartBuilderModal in prospect mode. Separate from CustomerSearch so reps can
// kick off a cart for a lead that isn't in Salesforce yet (pre-signup
// prospecting — per Casey's ask). The builder ends by creating their account
// and emailing the invite; it stays open on the result, so onSent only tells
// the Dashboard to refresh the invites board.
function ProspectCartLauncher({ onClose, onSent }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [step2, setStep2] = useState(false);

  const canContinue = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  if (step2) {
    return (
      <CartBuilderModal
        prospect={{ email: email.trim().toLowerCase(), name: name.trim(), company: company.trim() }}
        onClose={onClose}
        onSaved={onSent}
      />
    );
  }

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, backdropFilter: 'blur(2px)' }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 'min(460px, 94vw)', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 14, zIndex: 101, boxShadow: '0 16px 48px rgba(0,0,0,0.5)' }}>
        <div style={{ padding: '20px 24px 12px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>New prospect cart</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
              For someone without a Squish account. We create it with this cart in it and email them a link to finish.
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>
        <div style={{ padding: '16px 24px' }}>
          <label style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: 4 }}>Prospect email *</label>
          <input
            value={email}
            onChange={e => setEmail(e.target.value)}
            autoFocus
            placeholder="name@company.com"
            style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 12px', color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: 13, outline: 'none', marginBottom: 14 }}
          />
          <div style={{ display: 'flex', gap: 12, marginBottom: 4 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: 4 }}>Name</label>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="Jane Doe" style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 12px', color: 'var(--text)', fontFamily: 'var(--font)', fontSize: 13, outline: 'none' }} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: 4 }}>Company</label>
              <input value={company} onChange={e => setCompany(e.target.value)} placeholder="Acme Inc" style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 12px', color: 'var(--text)', fontFamily: 'var(--font)', fontSize: 13, outline: 'none' }} />
            </div>
          </div>
        </div>
        <div style={{ padding: '12px 24px 16px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', background: 'transparent', border: '1px solid var(--border)', color: 'var(--text3)', borderRadius: 6, fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer' }}>Cancel</button>
          <button
            onClick={() => setStep2(true)}
            disabled={!canContinue}
            style={{ padding: '8px 20px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: canContinue ? 'pointer' : 'not-allowed', fontFamily: 'var(--font)', opacity: canContinue ? 1 : 0.5 }}
          >
            Next: pick items →
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Main Dashboard ──────────────────────────────────────────────────────────
export default function Dashboard({ onPickCustomer, onNavigateToTicket }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [tab, setTab]         = useState(null); // default set by role once loaded
  const [prospectCartOpen, setProspectCartOpen] = useState(false);
  const [invitesVersion, setInvitesVersion] = useState(0);

  // Extracted from the mount effect so a stage change can refetch — moving a
  // deal to Closed Lost has to drop it out of "My Pipeline" immediately, or the
  // rep is looking at a board that disagrees with Salesforce.
  const load = useCallback(({ keepTab = false } = {}) => {
    return fetch(`${API}/api/dashboard`, { headers: getHeaders() })
      .then(r => r.json())
      .then(d => {
        if (d.error) { setError(d.error); setLoading(false); return; }
        setData(d);
        if (!keepTab) {
          // Default tab based on role: support → Support, sales → Sales, admin → Analytics.
          const r = d.role || 'unknown';
          setTab(r === 'sales' ? 'sales' : r === 'admin' ? 'support' : 'support');
        }
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const role = data?.role || 'unknown';
  const isAdmin = role === 'admin';
  const stats = data?.stats || {};

  // Build tab list based on role. Admin sees all three.
  const tabs = [
    { key: 'support', label: 'Support' },
    { key: 'sales',   label: 'Sales' },
    ...(isAdmin ? [{ key: 'analytics', label: 'Analytics' }] : []),
  ];

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <h1 className="page-title" style={{ margin: 0 }}>Dashboard</h1>
            {data?.staff && (
              <span
                className="badge"
                style={{ background: roleBadgeColor(role) + '22', color: roleBadgeColor(role), border: `1px solid ${roleBadgeColor(role)}55`, fontFamily: 'var(--mono)' }}
                title={data.staff.profileName || 'Role unknown'}
              >
                {roleLabel(role)}{data.staff.name ? ` · ${data.staff.name}` : ''}
              </span>
            )}
          </div>
          {/* Managed accounts used to ride here because the strip had no room
              for it. The company block on the Analytics tab carries it now, and
              printing it twice is the duplication this layout was cleaning up. */}
          <p className="page-subtitle" style={{ margin: 0 }}>Live from Salesforce</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button data-write
            onClick={() => setProspectCartOpen(true)}
            style={{ padding: '8px 14px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text2)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)' }}
          >
            + Prospect Cart
          </button>
          <CustomerSearch onPick={onPickCustomer} />
        </div>
      </div>

      {prospectCartOpen && (
        <ProspectCartLauncher onClose={() => setProspectCartOpen(false)} onSent={() => setInvitesVersion(v => v + 1)} />
      )}

      {error && <div className="error-state">⚠ {error}</div>}
      {loading && <Loading />}

      {data && <div className="dash-layout"><div className="dash-main">
        {/* Top stat row — always visible */}
        <KpiStrip stats={stats} sales={data.sales} staff={data.staff} isAdmin={isAdmin} />

        {/* Tabs. The switcher used to sit alone on its own line; the refresh
            control shares the row so the band isn't 40px of empty. */}
        <div className="tab-row">
          <div className="tab-bar">
            {tabs.map(t => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`tab-btn${tab === t.key ? ' is-active' : ''}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <button className="tab-refresh" onClick={() => load({ keepTab: true })}>refresh</button>
        </div>

        {tab === 'support'   && <SupportTab support={data.support} onPickCustomer={onPickCustomer} onNavigateToTicket={onNavigateToTicket} />}
        {tab === 'sales'     && <SalesTab sales={data.sales} onPickCustomer={onPickCustomer} onDealChanged={() => load({ keepTab: true })} onCartCancelled={() => load({ keepTab: true })} invitesVersion={invitesVersion} />}
        {tab === 'analytics' && isAdmin && <AnalyticsTab analytics={data.analytics} onPickCustomer={onPickCustomer} />}
      </div>

        {/* Today column — the staff follow-up queue (Salesforce Tasks written
            by squish-backend on signup / sales inquiry / callback) plus what
            came in overnight. Spans tabs; below 1280px it stacks above them. */}
        <div className="dash-side">
          <TodayRail
            followUps={data.followUps}
            staffName={data.staff?.name || null}
            onPickCustomer={onPickCustomer}
            onNavigateToTicket={onNavigateToTicket}
          />
          <Overnight
            overnight={data.overnight}
            onPickCustomer={onPickCustomer}
            onNavigateToTicket={onNavigateToTicket}
          />
        </div>
      </div>}
    </div>
  );
}
